import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { validateForm, VALIDATION_RULES, ErrorMessage } from '../../utils/formValidation';
import profileService from '../../api/services/profileService';
import authService from '../../api/services/authService';
import fileUploadService from '../../api/services/fileUploadService';
import {
  Mail,
  Edit,
  Key,
  Check,
  X,
  ArrowLeft,
  Loader,
  Camera,
  MapPin,
  AlertCircle,
  Plus,
  Trash2,
  Calendar,
  Eye,
  EyeOff
} from 'lucide-react';
import Button from '../../components/common/Button/Button';
import { Country, State, City } from 'country-state-city';
import LocationSelect from '../../components/common/LocationSelect/LocationSelect';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStep, setPasswordStep] = useState(1);
  const [passwordOTP, setPasswordOTP] = useState('');
  const [passwordOTPTimer, setPasswordOTPTimer] = useState(0);
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [showCurrentEmailOTP, setShowCurrentEmailOTP] = useState(false);
  const [showNewEmailModal, setShowNewEmailModal] = useState(false);
  const [showNewEmailOTP, setShowNewEmailOTP] = useState(false);
  const [showEmailUpdated, setShowEmailUpdated] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);

  // Profile Picture Upload State
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      const result = await fileUploadService.uploadProfilePicture(file);

      if (result.success) {
        // Immediate update to backend
        const updateResponse = await profileService.updateProfile({
          systemInfo: {
            profilePicture: result.url
          }
        });

        if (updateResponse.responseCode === 200) {
          setProfileData(prev => ({
            ...prev,
            profileImage: result.url
          }));

          // Update global user context so header updates immediately
          updateUser({
            ...user,
            profilePicture: result.url
          });

          toast.success('Profile picture updated successfully');
        } else {
          toast.error('Failed to update profile picture association');
        }
      } else {
        toast.error(result.error || 'Failed to upload image');
      }
    } catch (error) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const confirmRemoveProfileImage = async () => {
    setShowDeleteConfirm(false); // Close modal
    setUploading(true);

    try {
      const updateResponse = await profileService.updateProfile({
        systemInfo: {
          profilePicture: null
        }
      });

      if (updateResponse.responseCode === 200) {
        setProfileData(prev => ({
          ...prev,
          profileImage: null
        }));

        updateUser({
          ...user,
          profilePicture: null
        });

        toast.success('Profile picture removed');
      } else {
        toast.error('Failed to remove profile picture');
      }
    } catch (error) {
      toast.error('Failed to remove profile picture');
    } finally {
      setUploading(false);
    }
  };

  // Countdown timer states
  const [currentEmailTimer, setCurrentEmailTimer] = useState(0);
  const [newEmailTimer, setNewEmailTimer] = useState(0);

  // Form states
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    currentEmailOTP: '',
    newEmailOTP: ''
  });
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    personalPhone: '',
    personalEmail: '',
    street: '',
    city: null,
    state: null,
    country: null,
    pincode: '',
    permanentStreet: '',
    permanentCity: null,
    permanentState: null,
    permanentCountry: null,
    permanentPincode: '',
    sameAsCurrentAddress: false
  });
  const [errors, setErrors] = useState({});

  // Dynamic profile data from backend
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    personalEmail: '',
    officialEmail: '',
    personalPhone: '',
    officialPhone: '',
    currentAddress: {
      street: '',
      city: null,
      state: null,
      country: null,
      pincode: ''
    },
    permanentAddress: {
      street: '',
      city: null,
      state: null,
      country: null,
      pincode: ''
    },
    joinDate: '',
    role: '',
    department: '',
    profileImage: null
  });

  // Address options for cascading location dropdowns
  const [personalCountryOptions, setPersonalCountryOptions] = useState([]);
  const [personalStateOptions, setPersonalStateOptions] = useState([]);
  const [personalCityOptions, setPersonalCityOptions] = useState([]);
  const [permanentStateOptions, setPermanentStateOptions] = useState([]);
  const [permanentCityOptions, setPermanentCityOptions] = useState([]);

  // Load profile data on component mount or location change
  useEffect(() => {
    loadProfileData();
  }, [location.pathname]);

  // Countdown timer effects
  useEffect(() => {
    let interval = null;
    if (currentEmailTimer > 0) {
      interval = setInterval(() => {
        setCurrentEmailTimer(prev => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [currentEmailTimer]);

  useEffect(() => {
    let interval = null;
    if (passwordOTPTimer > 0) {
      interval = setInterval(() => {
        setPasswordOTPTimer(prev => prev - 1);
      }, 1000);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [passwordOTPTimer]);

  useEffect(() => {
    let interval;
    if (newEmailTimer > 0) {
      interval = setInterval(() => {
        setNewEmailTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [newEmailTimer]);

  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const formattedCountries = allCountries.map((country) => ({
      id: country.isoCode,
      name: country.name,
      isoCode: country.isoCode,
      ...country
    }));
    setPersonalCountryOptions(formattedCountries);
  }, []);

  useEffect(() => {
    if (profileForm.country && typeof profileForm.country === 'object' && profileForm.country.isoCode) {
      const states = State.getStatesOfCountry(profileForm.country.isoCode);
      const formattedStates = states.map((state) => ({
        id: state.isoCode,
        name: state.name,
        isoCode: state.isoCode,
        countryCode: state.countryCode,
        ...state
      }));
      setPersonalStateOptions(formattedStates);
    } else {
      setPersonalStateOptions([]);
    }
  }, [profileForm.country]);

  useEffect(() => {
    if (profileForm.country && typeof profileForm.country === 'object' && profileForm.state && typeof profileForm.state === 'object') {
      const cities = City.getCitiesOfState(profileForm.country.isoCode, profileForm.state.isoCode);
      const formattedCities = cities.map((city) => ({
        id: `${city.name}-${city.stateCode}`,
        name: city.name,
        stateCode: city.stateCode,
        countryCode: city.countryCode,
        ...city
      }));
      setPersonalCityOptions(formattedCities);
    } else {
      setPersonalCityOptions([]);
    }
  }, [profileForm.country, profileForm.state]);

  useEffect(() => {
    if (profileForm.permanentCountry && typeof profileForm.permanentCountry === 'object' && profileForm.permanentCountry.isoCode) {
      const states = State.getStatesOfCountry(profileForm.permanentCountry.isoCode);
      const formattedStates = states.map((state) => ({
        id: state.isoCode,
        name: state.name,
        isoCode: state.isoCode,
        countryCode: state.countryCode,
        ...state
      }));
      setPermanentStateOptions(formattedStates);
    } else {
      setPermanentStateOptions([]);
    }
  }, [profileForm.permanentCountry]);

  useEffect(() => {
    if (profileForm.permanentCountry && typeof profileForm.permanentCountry === 'object' && profileForm.permanentState && typeof profileForm.permanentState === 'object') {
      const cities = City.getCitiesOfState(profileForm.permanentCountry.isoCode, profileForm.permanentState.isoCode);
      const formattedCities = cities.map((city) => ({
        id: `${city.name}-${city.stateCode}`,
        name: city.name,
        stateCode: city.stateCode,
        countryCode: city.countryCode,
        ...city
      }));
      setPermanentCityOptions(formattedCities);
    } else {
      setPermanentCityOptions([]);
    }
  }, [profileForm.permanentCountry, profileForm.permanentState]);

  useEffect(() => {
    if (profileForm.sameAsCurrentAddress) {
      setProfileForm((prev) => ({
        ...prev,
        permanentStreet: prev.street,
        permanentCity: prev.city,
        permanentState: prev.state,
        permanentCountry: prev.country,
        permanentPincode: prev.pincode
      }));
    }
  }, [
    profileForm.sameAsCurrentAddress,
    profileForm.street,
    profileForm.city,
    profileForm.state,
    profileForm.country,
    profileForm.pincode
  ]);

  // Helper function to format timer
  const formatTimer = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatDateForInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const normalizeLocationObject = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      if (!value.name) return null;
      return {
        id: value.id || value.isoCode || value.name,
        name: value.name,
        isoCode: value.isoCode,
        stateCode: value.stateCode || value.state_code,
        ...value
      };
    }

    const strValue = String(value).trim();
    if (!strValue || strValue === 'Not provided') return null;
    return { id: strValue, name: strValue };
  };

  const normalizeAddress = (address) => ({
    street: address?.street && address.street !== 'Not provided' ? address.street : '',
    city: normalizeLocationObject(address?.city),
    state: normalizeLocationObject(address?.state),
    country: normalizeLocationObject(address?.country),
    pincode: address?.pincode && address.pincode !== '000000' ? String(address.pincode) : ''
  });

  const profileHasChanges = (nextForm) => (
    nextForm.firstName !== profileData.firstName ||
    nextForm.lastName !== profileData.lastName ||
    nextForm.dateOfBirth !== (profileData.dateOfBirth || '') ||
    nextForm.gender !== (profileData.gender || '') ||
    nextForm.personalPhone !== profileData.personalPhone ||
    nextForm.personalEmail !== profileData.personalEmail ||
    nextForm.street !== (profileData.currentAddress?.street || '') ||
    (nextForm.city?.name || '') !== (profileData.currentAddress?.city?.name || '') ||
    (nextForm.state?.name || '') !== (profileData.currentAddress?.state?.name || '') ||
    (nextForm.country?.name || '') !== (profileData.currentAddress?.country?.name || '') ||
    nextForm.pincode !== (profileData.currentAddress?.pincode || '') ||
    nextForm.permanentStreet !== (profileData.permanentAddress?.street || '') ||
    (nextForm.permanentCity?.name || '') !== (profileData.permanentAddress?.city?.name || '') ||
    (nextForm.permanentState?.name || '') !== (profileData.permanentAddress?.state?.name || '') ||
    (nextForm.permanentCountry?.name || '') !== (profileData.permanentAddress?.country?.name || '') ||
    nextForm.permanentPincode !== (profileData.permanentAddress?.pincode || '')
  );

  const loadProfileData = async () => {
    try {
      setProfileLoading(true);

      const response = await profileService.getProfile();

      if (response.responseCode === 200) {
        const data = response.data;

        const normalizedCurrentAddress = normalizeAddress(data.personalInfo?.currentAddress);
        const normalizedPermanentAddress = normalizeAddress(data.personalInfo?.permanentAddress);

        const profileInfo = {
          firstName: data.personalInfo?.firstName || user?.firstName || '',
          lastName: data.personalInfo?.lastName || user?.lastName || '',
          dateOfBirth: formatDateForInput(data.personalInfo?.dateOfBirth || user?.dateOfBirth || ''),
          gender: data.personalInfo?.gender || user?.gender || '',
          personalEmail: data.personalInfo?.email || user?.email || '',
          officialEmail: data.personalInfo?.officialEmail || '',
          personalPhone: data.personalInfo?.personalPhone || '',
          officialPhone: data.personalInfo?.officialPhone || '',
          currentAddress: normalizedCurrentAddress,
          permanentAddress: normalizedPermanentAddress,
          joinDate: data.employmentInfo?.joinDate || '',
          role: data.employmentInfo?.position || user?.role || '',
          department: data.employmentInfo?.department || user?.department || '',
          profileImage: data.systemInfo?.profilePicture || data.profileImage || null
        };

        setProfileData(profileInfo);
        setProfileForm({
          firstName: profileInfo.firstName,
          lastName: profileInfo.lastName,
          dateOfBirth: profileInfo.dateOfBirth,
          gender: profileInfo.gender,
          personalEmail: profileInfo.personalEmail,
          personalPhone: profileInfo.personalPhone,
          street: profileInfo.currentAddress.street,
          city: profileInfo.currentAddress.city,
          state: profileInfo.currentAddress.state,
          country: profileInfo.currentAddress.country,
          pincode: profileInfo.currentAddress.pincode,
          permanentStreet: profileInfo.permanentAddress.street,
          permanentCity: profileInfo.permanentAddress.city,
          permanentState: profileInfo.permanentAddress.state,
          permanentCountry: profileInfo.permanentAddress.country,
          permanentPincode: profileInfo.permanentAddress.pincode,
          sameAsCurrentAddress: false
        });

        // Sync with global auth context so header refreshes
        updateUser({
          ...user,
          firstName: profileInfo.firstName,
          lastName: profileInfo.lastName,
          email: profileInfo.personalEmail,
          dateOfBirth: profileInfo.dateOfBirth,
          gender: profileInfo.gender,
          profilePicture: profileInfo.profileImage // Header uses profilePicture
        });
      } else {
        toast.error('Failed to load profile data');
      }
    } catch (error) {
      toast.error('Failed to load profile data');
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handlePasswordBlur = (e) => {
    const { name, value } = e.target;

    if (name === 'currentPassword') {
      if (!value || !value.trim()) {
        setErrors(prev => ({ ...prev, currentPassword: 'Current Password is required.' }));
      } else {
        setErrors(prev => ({ ...prev, currentPassword: '' }));
      }
    } else if (name === 'newPassword') {
      if (!value || !value.trim()) {
        setErrors(prev => ({ ...prev, newPassword: 'New Password is required.' }));
      } else if (value.length < 8) {
        setErrors(prev => ({ ...prev, newPassword: 'New Password must be at least 8 characters long.' }));
      } else {
        setErrors(prev => ({ ...prev, newPassword: '' }));
      }

      if (passwordForm.confirmPassword) {
        if (value !== passwordForm.confirmPassword) {
          setErrors(prev => ({ ...prev, confirmPassword: 'New password and confirm password do not match.' }));
        } else {
          setErrors(prev => ({ ...prev, confirmPassword: '' }));
        }
      }
    } else if (name === 'confirmPassword') {
      if (!value || !value.trim()) {
        setErrors(prev => ({ ...prev, confirmPassword: 'Confirm Password is required.' }));
      } else if (passwordForm.newPassword && value !== passwordForm.newPassword) {
        setErrors(prev => ({ ...prev, confirmPassword: 'New password and confirm password do not match.' }));
      } else {
        setErrors(prev => ({ ...prev, confirmPassword: '' }));
      }
    }
  };

  const handleEmailChange = (e) => {
    const { name, value } = e.target;
    setEmailForm(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;

    // Handle different input types
    let processedValue = value;
    if (name === 'personalPhone') {
      // Allow one leading '+' and max 15 total characters
      let normalized = value.replace(/[^\d+]/g, '');
      if (normalized.includes('+')) {
        normalized = (normalized[0] === '+' ? '+' : '') + normalized.replace(/\+/g, '');
      }

      const digitLimit = normalized.startsWith('+') ? 14 : 15;
      const digitsOnly = normalized.replace(/\D/g, '').slice(0, digitLimit);
      processedValue = normalized.startsWith('+') ? `+${digitsOnly}` : digitsOnly;
    } else if (name === 'firstName' || name === 'lastName') {
      // Remove all non-letter characters (allow letters, spaces, hyphens, apostrophes)
      processedValue = value.replace(/[^a-zA-Z\s\-']/g, '');
    }

    setProfileForm(prev => {
      const nextForm = {
        ...prev,
        [name]: processedValue
      };
      setHasChanges(profileHasChanges(nextForm));
      return nextForm;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSameAddressToggle = (e) => {
    const isChecked = e.target.checked;
    setProfileForm((prev) => {
      const nextForm = isChecked
        ? {
          ...prev,
          sameAsCurrentAddress: true,
          permanentStreet: prev.street,
          permanentCity: prev.city,
          permanentState: prev.state,
          permanentCountry: prev.country,
          permanentPincode: prev.pincode
        }
        : {
          ...prev,
          sameAsCurrentAddress: false
        };

      setHasChanges(profileHasChanges(nextForm));
      return nextForm;
    });
  };

  const handleProfileKeyDown = (e) => {
    const { name } = e.target;

    // Allow navigation and control keys for all fields
    if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)) {
      return;
    }

    if (name === 'personalPhone') {
      // Allow digits and one leading '+' for phone field
      const isNumberKey = (!e.shiftKey && e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105);
      const isPlusKey = e.key === '+' || e.keyCode === 107 || (e.keyCode === 187 && e.shiftKey);
      const cursorAtStart = (e.target.selectionStart || 0) === 0;
      const hasPlus = String(e.target.value || '').includes('+');

      if (!isNumberKey && !(isPlusKey && cursorAtStart && !hasPlus)) {
        e.preventDefault();
      }
    } else if (name === 'firstName' || name === 'lastName') {
      // Prevent numbers and special characters for name fields
      // Allow: letters (A-Z, a-z), space (32), hyphen (189), apostrophe (222)
      if (!((e.keyCode >= 65 && e.keyCode <= 90) || // A-Z
        (e.keyCode >= 97 && e.keyCode <= 122) || // a-z
        e.keyCode === 32 || // space
        e.keyCode === 189 || // hyphen
        e.keyCode === 222)) { // apostrophe
        e.preventDefault();
      }
    }
  };

  const validatePasswordForm = () => {
    const validationRules = {
      currentPassword: VALIDATION_RULES.required,
      newPassword: {
        required: true,
        minLength: 8
      },
      confirmPassword: VALIDATION_RULES.confirmPassword
    };

    let { errors, isValid } = validateForm(passwordForm, validationRules);

    if (passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword) {
      errors.confirmPassword = 'New password and confirm password do not match.';
      toast.error('New password and confirm password do not match.');
      isValid = false;
    }

    setErrors(errors);
    return isValid;
  };

  const validateEmailForm = () => {
    const validationRules = {
      newEmail: { ...VALIDATION_RULES.email, maxLength: 254 }
    };

    const { errors, isValid } = validateForm(emailForm, validationRules);
    setErrors(errors);
    return isValid;
  };

  const validateProfileForm = () => {
    const validationRules = {
      firstName: {
        required: true,
        minLength: 2,
        maxLength: 50,
        pattern: /^[a-zA-Z\s'-]+$/,
        patternMessage: 'First name can only contain letters, spaces, hyphens, and apostrophes'
      },
      lastName: {
        required: true,
        minLength: 2,
        maxLength: 50,
        pattern: /^[a-zA-Z\s'-]+$/,
        patternMessage: 'Last name can only contain letters, spaces, hyphens, and apostrophes'
      },
      dateOfBirth: {
        required: false,
        pattern: /^\d{4}-\d{2}-\d{2}$/,
        patternMessage: 'Please select a valid date'
      },
      gender: {
        required: false,
        pattern: /^(male|female|other)?$/,
        patternMessage: 'Please select a valid gender'
      },
      personalPhone: {
        required: true,
        pattern: /^(?:\+\d{10,14}|\d{10,15})$/,
        patternMessage: 'Please enter a valid phone number (10-15 characters, + allowed only at start)'
      },
      personalEmail: {
        required: true,
        pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        patternMessage: 'Please enter a valid email address'
      },
      pincode: {
        required: false,
        maxLength: 15
      },
      permanentPincode: {
        required: false,
        maxLength: 15
      }
    };

    const { errors, isValid } = validateForm(profileForm, validationRules);
    setErrors(errors);
    return isValid;
  };

  const handleSendPasswordOTP = async (e) => {
    e.preventDefault();

    if (!validatePasswordForm()) return;

    const officialEmail = user?.email || profileData?.officialEmail;
    if (!officialEmail) {
      toast.error('Official email not found in user profile.');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.sendOTP({ emailOrPhone: officialEmail });

      if (response.success) {
        toast.success(response.message || `OTP sent to official email: ${officialEmail}`);
        setPasswordStep(2);
        setPasswordOTPTimer(30);
      } else {
        toast.error(response.error || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPasswordOTPAndSubmit = async (e) => {
    e.preventDefault();

    if (!passwordOTP || passwordOTP.trim().length !== 6) {
      setErrors(prev => ({ ...prev, passwordOTP: 'Please enter a 6-digit OTP' }));
      return;
    }

    const officialEmail = user?.email || profileData?.officialEmail;
    if (!officialEmail) {
      toast.error('Official email not found in user collection.');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.resetPassword({
        emailOrPhone: officialEmail,
        otp: passwordOTP.trim(),
        newPassword: passwordForm.newPassword
      });

      if (response.success) {
        toast.success('Password changed successfully!');
        handleCloseChangePasswordModal();
      } else {
        toast.error(response.error || 'Failed to change password. Invalid or expired OTP.');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendPasswordOTP = async () => {
    const officialEmail = user?.email || profileData?.officialEmail;
    if (!officialEmail) {
      toast.error('Official email not found in user collection.');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.sendOTP({ emailOrPhone: officialEmail });
      if (response.success) {
        toast.success(`OTP resent to official email: ${officialEmail}`);
        setPasswordOTPTimer(30);
      } else {
        toast.error(response.error || 'Failed to resend OTP');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseChangePasswordModal = () => {
    setShowChangePassword(false);
    setPasswordStep(1);
    setPasswordOTP('');
    setPasswordOTPTimer(0);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors(prev => ({
      ...prev,
      currentPassword: null,
      newPassword: null,
      confirmPassword: null,
      passwordOTP: null
    }));
  };

  const handleChangeEmail = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const response = await profileService.sendCurrentEmailOTP();

      if (response.responseCode === 200) {
        toast.success('OTP sent to your current email');
        setShowChangeEmail(false);
        setShowCurrentEmailOTP(true);
        setCurrentEmailTimer(300); // 5 minutes = 300 seconds
      } else {
        toast.error(response.responseMessage || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCurrentEmailOTP = async (e) => {
    e.preventDefault();

    if (!emailForm.currentEmailOTP.trim()) {
      setErrors({ currentEmailOTP: 'Please enter the OTP' });
      return;
    }

    if (emailForm.currentEmailOTP.length !== 6) {
      setErrors({ currentEmailOTP: 'OTP must be 6 digits' });
      return;
    }

    setLoading(true);

    try {
      const response = await profileService.verifyCurrentEmailOTP({
        otp: emailForm.currentEmailOTP
      });

      if (response.responseCode === 200) {
        toast.success('Current email verified successfully!');
        setShowCurrentEmailOTP(false);
        setShowNewEmailModal(true);

        // Reset current email OTP
        setEmailForm(prev => ({
          ...prev,
          currentEmailOTP: ''
        }));
      } else {
        toast.error(response.responseMessage || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleNewEmailOTP = async (e) => {
    e.preventDefault();

    if (!emailForm.newEmailOTP.trim()) {
      setErrors({ newEmailOTP: 'Please enter the OTP' });
      return;
    }

    if (emailForm.newEmailOTP.length !== 6) {
      setErrors({ newEmailOTP: 'OTP must be 6 digits' });
      return;
    }

    setLoading(true);

    try {
      const response = await profileService.verifyNewEmailOTP({
        newEmail: emailForm.newEmail,
        otp: emailForm.newEmailOTP
      });

      if (response.responseCode === 200) {
        toast.success('Email changed successfully!');
        setShowNewEmailOTP(false);
        setShowEmailUpdated(true);

        // Update profile data
        setProfileData(prev => ({
          ...prev,
          email: emailForm.newEmail
        }));

        // Update user context
        updateUser({
          ...user,
          email: emailForm.newEmail
        });

        // Reset form
        setEmailForm({
          newEmail: '',
          currentEmailOTP: '',
          newEmailOTP: ''
        });
      } else {
        toast.error(response.responseMessage || 'Invalid OTP');
      }
    } catch (error) {
      toast.error('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendNewEmailOTP = async () => {
    if (!emailForm.newEmail.trim()) {
      setErrors({ newEmail: 'Please enter new email address' });
      return;
    }

    if (!validateEmailForm()) return;

    setLoading(true);

    try {
      const response = await profileService.sendNewEmailOTP({
        newEmail: emailForm.newEmail
      });

      if (response.responseCode === 200) {
        toast.success('OTP sent to your new email');
        setShowNewEmailModal(false);
        setShowNewEmailOTP(true);
        setNewEmailTimer(300); // 5 minutes = 300 seconds
      } else {
        toast.error(response.responseMessage || 'Failed to send OTP');
      }
    } catch (error) {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    if (!validateProfileForm()) return;

    setLoading(true);

    try {
      const response = await profileService.updateProfile({
        personalInfo: {
          firstName: profileForm.firstName.trim(),
          lastName: profileForm.lastName.trim(),
          dateOfBirth: profileForm.dateOfBirth || undefined,
          gender: profileForm.gender || undefined
        },
        contactInfo: {
          personalPhone: profileForm.personalPhone.trim(),
          personalEmail: profileForm.personalEmail.trim(),
          currentAddress: {
            street: profileForm.street || '',
            city: profileForm.city || { name: '' },
            state: profileForm.state || { name: '' },
            country: profileForm.country || { name: '' },
            pincode: profileForm.pincode || ''
          },
          permanentAddress: {
            street: profileForm.permanentStreet || '',
            city: profileForm.permanentCity || { name: '' },
            state: profileForm.permanentState || { name: '' },
            country: profileForm.permanentCountry || { name: '' },
            pincode: profileForm.permanentPincode || ''
          }
        }
      });

      if (response.responseCode === 200) {
        // Update profile data
        setProfileData(prev => ({
          ...prev,
          firstName: profileForm.firstName.trim(),
          lastName: profileForm.lastName.trim(),
          dateOfBirth: profileForm.dateOfBirth || '',
          gender: profileForm.gender || '',
          personalPhone: profileForm.personalPhone.trim(),
          personalEmail: profileForm.personalEmail.trim(),
          currentAddress: {
            street: profileForm.street || '',
            city: profileForm.city,
            state: profileForm.state,
            country: profileForm.country,
            pincode: profileForm.pincode || ''
          },
          permanentAddress: {
            street: profileForm.permanentStreet || '',
            city: profileForm.permanentCity,
            state: profileForm.permanentState,
            country: profileForm.permanentCountry,
            pincode: profileForm.permanentPincode || ''
          }
        }));

        // Update user context
        updateUser({
          ...user,
          firstName: profileForm.firstName.trim(),
          lastName: profileForm.lastName.trim(),
          email: profileForm.personalEmail.trim(),
          dateOfBirth: profileForm.dateOfBirth || '',
          gender: profileForm.gender || ''
        });

        setIsEditing(false);
        setHasChanges(false);
        toast.success('Profile updated successfully!');
      } else {
        toast.error(response.responseMessage || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setProfileForm({
      firstName: profileData.firstName,
      lastName: profileData.lastName,
      dateOfBirth: profileData.dateOfBirth || '',
      gender: profileData.gender || '',
      personalEmail: profileData.personalEmail,
      personalPhone: profileData.personalPhone,
      street: profileData.currentAddress?.street || '',
      city: profileData.currentAddress?.city || null,
      state: profileData.currentAddress?.state || null,
      country: profileData.currentAddress?.country || null,
      pincode: profileData.currentAddress?.pincode || '',
      permanentStreet: profileData.permanentAddress?.street || '',
      permanentCity: profileData.permanentAddress?.city || null,
      permanentState: profileData.permanentAddress?.state || null,
      permanentCountry: profileData.permanentAddress?.country || null,
      permanentPincode: profileData.permanentAddress?.pincode || '',
      sameAsCurrentAddress: false
    });
    setIsEditing(false);
    setHasChanges(false);
    setErrors({});
  };

  const renderProfileTab = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Personal</h2>
          {!isEditing ? (
            <Button
              onClick={() => setIsEditing(true)}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <Edit className="w-4 h-4" />
              <span>Edit</span>
            </Button>
          ) : (
            <div className="flex space-x-2">
              <Button
                onClick={handleSaveProfile}
                disabled={loading || !hasChanges}
                className={`flex items-center space-x-2 ${hasChanges ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              >
                {loading ? 'Saving...' : hasChanges ? 'Save Changes' : 'No Changes'}
              </Button>
              <Button
                onClick={handleCancelEdit}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </Button>
            </div>
          )}
        </div>

        {/* Profile Picture Section */}
        <div className="flex items-center space-x-6 mb-8 px-6">
          <div className="relative w-24 h-24">
            <div className="w-full h-full rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-100 relative group">
              {profileData.profileImage ? (
                <img
                  src={profileData.profileImage.startsWith('http') ? profileData.profileImage : `${process.env.REACT_APP_API_BASE_URL}/uploads/${profileData.profileImage}`}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-blue-100 text-blue-600 font-bold text-3xl">
                  {profileData.firstName?.[0]}{profileData.lastName?.[0]}
                </div>
              )}

              {/* Overlay for uploading - Only visible when editing */}
              {isEditing && (
                <div
                  className={`absolute inset-0 bg-black/40 flex items-center justify-center cursor-pointer transition-opacity ${uploading ? 'opacity-100' : 'opacity-100'}`}
                  onClick={() => !uploading && fileInputRef.current?.click()}
                  title="Change Profile Picture"
                >
                  {uploading ? (
                    <Loader className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <Plus className="w-8 h-8 text-white" />
                  )}
                </div>
              )}
            </div>

            {/* Remove Button - Only when editing and image exists */}
            {isEditing && profileData.profileImage && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteConfirm(true);
                }}
                className="absolute -top-1 -right-1 w-7 h-7 flex items-center justify-center bg-white text-red-500 rounded-full shadow-md hover:bg-gray-50 border border-gray-200 z-10 transition-transform hover:scale-105"
                title="Remove Profile Picture"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}

            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={!isEditing}
            />
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {profileData.firstName} {profileData.lastName}
            </h3>
            <p className="text-sm text-gray-500">{profileData.role}</p>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 transform transition-all scale-100">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Remove Image?</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to remove your profile picture? This action cannot be undone.
                  </p>
                </div>
                <div className="flex space-x-3 w-full pt-2">
                  <Button
                    onClick={() => setShowDeleteConfirm(false)}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={confirmRemoveProfileImage}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSaveProfile}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={profileForm.firstName}
                onChange={handleProfileChange}
                onKeyDown={handleProfileKeyDown}
                disabled={!isEditing}
                maxLength={50}
                className={`w-full flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing
                  ? errors.firstName
                    ? 'border-red-500'
                    : 'border-blue-300'
                  : 'border-blue-300 bg-gray-50 text-gray-900'
                  }`}
                placeholder="Enter first name"
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {profileForm.firstName.length}/50 characters
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={profileForm.lastName}
                onChange={handleProfileChange}
                onKeyDown={handleProfileKeyDown}
                disabled={!isEditing}
                maxLength={50}
                className={`w-full flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing
                  ? errors.lastName
                    ? 'border-red-500'
                    : 'border-blue-300'
                  : 'border-blue-300 bg-gray-50 text-gray-900'
                  }`}
                placeholder="Enter last name"
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {profileForm.lastName.length}/50 characters
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personal Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="personalEmail"
                value={profileForm.personalEmail}
                onChange={handleProfileChange}
                disabled={!isEditing}
                maxLength={254}
                className={`w-full flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing
                  ? errors.personalEmail
                    ? 'border-red-500'
                    : 'border-blue-300'
                  : 'border-blue-300 bg-gray-50 text-gray-900'
                  }`}
                placeholder="Enter personal email"
              />
              {errors.personalEmail && (
                <p className="text-red-500 text-sm mt-1">{errors.personalEmail}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {profileForm.personalEmail.length}/254 characters
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Official Email
              </label>
              <input
                type="email"
                value={profileData.officialEmail}
                readOnly
                className="w-full flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 mt-1">Official email from company (read-only)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personal Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="personalPhone"
                value={profileForm.personalPhone}
                onChange={handleProfileChange}
                onKeyDown={handleProfileKeyDown}
                disabled={!isEditing}
                maxLength={15}
                className={`w-full flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing
                  ? errors.personalPhone
                    ? 'border-red-500'
                    : 'border-blue-300'
                  : 'border-blue-300 bg-gray-50 text-gray-900'
                  }`}
                placeholder="+911234567890"
              />
              {errors.personalPhone && (
                <p className="text-red-500 text-sm mt-1">{errors.personalPhone}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {String(profileForm.personalPhone || '').length}/15 characters
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Official Phone
              </label>
              <input
                type="text"
                value={profileData.officialPhone}
                readOnly
                className="w-full flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
                placeholder="N/A"
              />
              <p className="text-xs text-gray-500 mt-1">Official phone from company (read-only)</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date of Birth
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                <input
                  type="date"
                  name="dateOfBirth"
                  value={profileForm.dateOfBirth}
                  onChange={handleProfileChange}
                  disabled={!isEditing}
                  max={(() => {
                    const today = new Date();
                    const maxDate = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());
                    return maxDate.toISOString().split('T')[0];
                  })()}
                  className={`w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing
                    ? errors.dateOfBirth
                      ? 'border-red-500'
                      : 'border-blue-300'
                    : 'border-blue-300 bg-gray-50 text-gray-900'
                    }`}
                />
              </div>
              {errors.dateOfBirth && (
                <p className="text-red-500 text-sm mt-1">{errors.dateOfBirth}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gender
              </label>
              <select
                name="gender"
                value={profileForm.gender}
                onChange={handleProfileChange}
                disabled={!isEditing}
                className={`w-full px-3 py-2 h-11 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing
                  ? errors.gender
                    ? 'border-red-500'
                    : 'border-blue-300'
                  : 'border-blue-300 bg-gray-50 text-gray-900'
                  }`}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
              {errors.gender && (
                <p className="text-red-500 text-sm mt-1">{errors.gender}</p>
              )}
            </div>
            <div className="md:col-span-2 border-t border-gray-100 pt-6 mt-2">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center">
                <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                Current Address
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    type="text"
                    name="street"
                    value={profileForm.street || ''}
                    onChange={handleProfileChange}
                    disabled={!isEditing}
                    placeholder="Street, building, area"
                    className="w-full px-3 py-2 border border-blue-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  {!isEditing ? (
                    <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                      {profileForm.country?.name || 'Not provided'}
                    </div>
                  ) : (
                    <LocationSelect
                      options={personalCountryOptions}
                      value={profileForm.country}
                      onChange={(country) => {
                        setProfileForm((prev) => {
                          const nextForm = {
                            ...prev,
                            country,
                            state: null,
                            city: null
                          };
                          setHasChanges(profileHasChanges(nextForm));
                          return nextForm;
                        });
                      }}
                      placeholder="Select Country"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  {!isEditing ? (
                    <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                      {profileForm.state?.name || 'Not provided'}
                    </div>
                  ) : (
                    <LocationSelect
                      options={personalStateOptions}
                      value={profileForm.state}
                      disabled={!profileForm.country}
                      onChange={(state) => {
                        setProfileForm((prev) => {
                          const nextForm = {
                            ...prev,
                            state,
                            city: null
                          };
                          setHasChanges(profileHasChanges(nextForm));
                          return nextForm;
                        });
                      }}
                      placeholder="Select State"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  {!isEditing ? (
                    <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                      {profileForm.city?.name || 'Not provided'}
                    </div>
                  ) : (
                    <LocationSelect
                      options={personalCityOptions}
                      value={profileForm.city}
                      disabled={!profileForm.state}
                      onChange={(city) => {
                        setProfileForm((prev) => {
                          const nextForm = {
                            ...prev,
                            city
                          };
                          setHasChanges(profileHasChanges(nextForm));
                          return nextForm;
                        });
                      }}
                      placeholder="Select City"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                  <input
                    type="text"
                    name="pincode"
                    value={profileForm.pincode || ''}
                    onChange={handleProfileChange}
                    disabled={!isEditing}
                    maxLength={15}
                    placeholder="Postal Code"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing
                      ? errors.pincode
                        ? 'border-red-500'
                        : 'border-blue-300'
                      : 'border-blue-300 bg-gray-50 text-gray-900'
                      }`}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.pincode && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.pincode}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 ml-auto">
                      {(profileForm.pincode || '').length}/15 chars
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 border-t border-gray-100 pt-6 mt-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center">
                  <MapPin className="h-4 w-4 mr-2 text-blue-600" />
                  Permanent Address
                </h3>
                <label className={`flex items-center space-x-2 select-none ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'}`}>
                  <input
                    type="checkbox"
                    checked={profileForm.sameAsCurrentAddress}
                    onChange={handleSameAddressToggle}
                    disabled={!isEditing}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-600">Same as Current Address</span>
                </label>
              </div>

              <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${profileForm.sameAsCurrentAddress ? 'opacity-75 pointer-events-none' : ''}`}>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street Address</label>
                  <input
                    type="text"
                    name="permanentStreet"
                    value={profileForm.permanentStreet || ''}
                    onChange={handleProfileChange}
                    readOnly={!isEditing || profileForm.sameAsCurrentAddress}
                    disabled={!isEditing}
                    placeholder="Street, building, area"
                    className={`w-full px-3 py-2 border border-blue-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 ${profileForm.sameAsCurrentAddress ? 'bg-gray-100' : ''}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                  {!isEditing ? (
                    <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                      {profileForm.permanentCountry?.name || 'Not provided'}
                    </div>
                  ) : (
                    <LocationSelect
                      options={personalCountryOptions}
                      value={profileForm.permanentCountry}
                      onChange={(country) => {
                        if (profileForm.sameAsCurrentAddress) return;
                        setProfileForm((prev) => {
                          const nextForm = {
                            ...prev,
                            permanentCountry: country,
                            permanentState: null,
                            permanentCity: null
                          };
                          setHasChanges(profileHasChanges(nextForm));
                          return nextForm;
                        });
                      }}
                      placeholder="Select Country"
                      disabled={profileForm.sameAsCurrentAddress}
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  {!isEditing ? (
                    <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                      {profileForm.permanentState?.name || 'Not provided'}
                    </div>
                  ) : (
                    <LocationSelect
                      options={permanentStateOptions}
                      value={profileForm.permanentState}
                      disabled={!profileForm.permanentCountry || profileForm.sameAsCurrentAddress}
                      onChange={(state) => {
                        if (profileForm.sameAsCurrentAddress) return;
                        setProfileForm((prev) => {
                          const nextForm = {
                            ...prev,
                            permanentState: state,
                            permanentCity: null
                          };
                          setHasChanges(profileHasChanges(nextForm));
                          return nextForm;
                        });
                      }}
                      placeholder="Select State"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  {!isEditing ? (
                    <div className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-900">
                      {profileForm.permanentCity?.name || 'Not provided'}
                    </div>
                  ) : (
                    <LocationSelect
                      options={permanentCityOptions}
                      value={profileForm.permanentCity}
                      disabled={!profileForm.permanentState || profileForm.sameAsCurrentAddress}
                      onChange={(city) => {
                        if (profileForm.sameAsCurrentAddress) return;
                        setProfileForm((prev) => {
                          const nextForm = {
                            ...prev,
                            permanentCity: city
                          };
                          setHasChanges(profileHasChanges(nextForm));
                          return nextForm;
                        });
                      }}
                      placeholder="Select City"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
                  <input
                    type="text"
                    name="permanentPincode"
                    value={profileForm.permanentPincode || ''}
                    onChange={handleProfileChange}
                    readOnly={!isEditing || profileForm.sameAsCurrentAddress}
                    disabled={!isEditing}
                    maxLength={15}
                    placeholder="Postal Code"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isEditing
                      ? errors.permanentPincode
                        ? 'border-red-500'
                        : 'border-blue-300'
                      : 'border-blue-300 bg-gray-50 text-gray-900'
                      } ${profileForm.sameAsCurrentAddress ? 'bg-gray-100' : ''}`}
                  />
                  <div className="flex justify-between items-center mt-1">
                    {errors.permanentPincode && (
                      <p className="text-sm text-red-600 flex items-center">
                        <AlertCircle className="h-4 w-4 mr-1" />
                        {errors.permanentPincode}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 ml-auto">
                      {(profileForm.permanentPincode || '').length}/15 chars
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* Action Buttons */}
      {!isEditing && (
        <div className="flex space-x-4">
          <Button
            onClick={() => setShowChangePassword(true)}
            className="flex items-center space-x-2"
          >
            <Key className="w-4 h-4" />
            <span>Change Password</span>
          </Button>
        </div>
      )}
    </div>
  );

  const renderChangePasswordModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            {passwordStep === 1 ? 'Change Password' : 'Verify OTP'}
          </h3>
          <button
            onClick={handleCloseChangePasswordModal}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {passwordStep === 1 ? (
          <form onSubmit={handleSendPasswordOTP} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${errors.currentPassword ? 'border-red-500' : 'border-blue-300'}`}
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showCurrentPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
              {errors.currentPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.currentPassword}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${errors.newPassword ? 'border-red-500' : 'border-blue-300'}`}
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showNewPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.newPassword}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Password must be at least 8 characters
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  onBlur={handlePasswordBlur}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${errors.confirmPassword ? 'border-red-500' : 'border-blue-300'}`}
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showConfirmPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
              <Button
                type="button"
                onClick={handleCloseChangePasswordModal}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={handleVerifyPasswordOTPAndSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              An OTP has been sent to your official email: <span className="font-semibold text-gray-800">{user?.email}</span>
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enter 6-Digit OTP <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                maxLength={6}
                value={passwordOTP}
                onChange={(e) => setPasswordOTP(e.target.value.replace(/\D/g, ''))}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.passwordOTP ? 'border-red-500' : 'border-blue-300'}`}
                placeholder="Enter 6-digit OTP"
              />
              {errors.passwordOTP && (
                <p className="text-red-500 text-sm mt-1">{errors.passwordOTP}</p>
              )}
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>
                {passwordOTPTimer > 0 ? `Resend code in ${passwordOTPTimer}s` : ''}
              </span>
              <button
                type="button"
                disabled={passwordOTPTimer > 0 || loading}
                onClick={handleResendPasswordOTP}
                className={`font-medium text-blue-600 hover:text-blue-800 ${passwordOTPTimer > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                Resend OTP
              </button>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="flex-1"
              >
                {loading ? 'Updating Password...' : 'Verify OTP & Update Password'}
              </Button>
              <Button
                type="button"
                onClick={() => setPasswordStep(1)}
                variant="outline"
                className="flex-1"
              >
                Back
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );

  const renderChangeEmailModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Verify Email</h3>
          <button
            onClick={() => {
              setShowChangeEmail(false);
              // Reset email form
              setEmailForm({
                newEmail: '',
                currentEmailOTP: '',
                newEmailOTP: ''
              });
              setErrors({});
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Current Personal Email
            </label>
            <input
              type="email"
              value={profileData.personalEmail}
              readOnly
              className="w-full px-3 py-2 border border-blue-300 rounded-md bg-gray-50 text-gray-600"
            />
          </div>


          <div className="flex space-x-3 pt-4">
            <Button
              onClick={handleChangeEmail}
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </Button>
            <Button
              onClick={() => {
                setShowChangeEmail(false);
                // Reset email form
                setEmailForm({
                  newEmail: '',
                  currentEmailOTP: '',
                  newEmailOTP: ''
                });
                setErrors({});
              }}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentEmailOTPScreen = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Verify</h3>
          <button
            onClick={() => {
              setShowCurrentEmailOTP(false);
              // Reset email form
              setEmailForm({
                newEmail: '',
                currentEmailOTP: '',
                newEmailOTP: ''
              });
              setErrors({});
              setCurrentEmailTimer(0);
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          We've sent a 6-digit OTP to your current personal email address: <strong>{profileData.personalEmail}</strong>
        </p>

        {currentEmailTimer > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-blue-700">
                OTP expires in: {formatTimer(currentEmailTimer)}
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleCurrentEmailOTP} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OTP <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="currentEmailOTP"
              value={emailForm.currentEmailOTP}
              onChange={handleEmailChange}
              maxLength={6}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest ${errors.currentEmailOTP ? 'border-red-500' : 'border-blue-300'
                }`}
              placeholder="000000"
            />
            {errors.currentEmailOTP && (
              <p className="text-red-500 text-sm mt-1">{errors.currentEmailOTP}</p>
            )}
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowCurrentEmailOTP(false);
                // Reset email form
                setEmailForm({
                  newEmail: '',
                  currentEmailOTP: '',
                  newEmailOTP: ''
                });
                setErrors({});
                setCurrentEmailTimer(0);
              }}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderNewEmailModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">New Email</h3>
          <button
            onClick={() => {
              setShowNewEmailModal(false);
              // Reset email form
              setEmailForm({
                newEmail: '',
                currentEmailOTP: '',
                newEmailOTP: ''
              });
              setErrors({});
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="newEmail"
              value={emailForm.newEmail}
              onChange={handleEmailChange}
              maxLength={254}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.newEmail ? 'border-red-500' : 'border-blue-300'
                }`}
              placeholder="Enter new email address"
            />
            {errors.newEmail && (
              <p className="text-red-500 text-sm mt-1">{errors.newEmail}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {emailForm.newEmail.length}/254 characters
            </p>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              onClick={handleSendNewEmailOTP}
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Sending OTP...' : 'Send OTP'}
            </Button>
            <Button
              onClick={() => {
                setShowNewEmailModal(false);
                // Reset email form
                setEmailForm({
                  newEmail: '',
                  currentEmailOTP: '',
                  newEmailOTP: ''
                });
                setErrors({});
              }}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNewEmailOTPScreen = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Verify</h3>
          <button
            onClick={() => {
              setShowNewEmailOTP(false);
              // Reset email form
              setEmailForm({
                newEmail: '',
                currentEmailOTP: '',
                newEmailOTP: ''
              });
              setErrors({});
              setNewEmailTimer(0);
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          We've sent a 6-digit OTP to your new email address: <strong>{emailForm.newEmail}</strong>
        </p>

        {newEmailTimer > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-blue-700">
                OTP expires in: {formatTimer(newEmailTimer)}
              </span>
            </div>
          </div>
        )}

        <form onSubmit={handleNewEmailOTP} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              OTP <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="newEmailOTP"
              value={emailForm.newEmailOTP}
              onChange={handleEmailChange}
              maxLength={6}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-lg tracking-widest ${errors.newEmailOTP ? 'border-red-500' : 'border-blue-300'
                }`}
              placeholder="000000"
            />
            {errors.newEmailOTP && (
              <p className="text-red-500 text-sm mt-1">{errors.newEmailOTP}</p>
            )}
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1"
            >
              {loading ? 'Verifying...' : 'Verify'}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowNewEmailOTP(false);
                // Reset email form
                setEmailForm({
                  newEmail: '',
                  currentEmailOTP: '',
                  newEmailOTP: ''
                });
                setErrors({});
                setNewEmailTimer(0);
              }}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderEmailUpdatedScreen = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Updated</h3>
        <p className="text-sm text-gray-600 mb-6">
          Your email address has been updated to {emailForm.newEmail}
        </p>
        <Button
          onClick={() => setShowEmailUpdated(false)}
          className="w-full"
        >
          Continue
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Profile</h1>
          </div>
          <p className="text-gray-600">Manage profile</p>
        </div>

        {/* Loading State */}
        {profileLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-3">
              <Loader className="w-6 h-6 animate-spin text-blue-600" />
              <span className="text-gray-600">Loading profile data...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Profile Content */}
            {renderProfileTab()}

            {/* Modals */}
            {showChangePassword && renderChangePasswordModal()}
            {showChangeEmail && renderChangeEmailModal()}
            {showCurrentEmailOTP && renderCurrentEmailOTPScreen()}
            {showNewEmailModal && renderNewEmailModal()}
            {showNewEmailOTP && renderNewEmailOTPScreen()}
            {showEmailUpdated && renderEmailUpdatedScreen()}
          </>
        )}
      </div>
    </div>
  );
};

export default Profile;
